/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LearningApiService from '../services/LearningApiService';

const LearningContext = createContext();

export const useLearning = () => {
  const context = useContext(LearningContext);
  if (!context) {
    throw new Error('useLearning must be used within a LearningProvider');
  }
  return context;
};

export const LearningProvider = ({ children }) => {
  // User learning data
  const [learningProfile, setLearningProfile] = useState(null);
  const [learningStats, setLearningStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  
  // Course data
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  
  // Content data
  const [engineeringBooks, setEngineeringBooks] = useState([]);
  const [virtualInternships, setVirtualInternships] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Initialize learning data
  useEffect(() => {
    initializeLearningData();
  }, []);

  const initializeLearningData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadLearningProfile(),
        loadLearningStats(),
        loadEnrolledCourses(),
        loadAvailableCourses(),
        loadEngineeringBooks(),
        loadVirtualInternships(),
        loadAchievements()
      ]);
    } catch (error) {
      console.error('Error initializing learning data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Profile management
  const loadLearningProfile = async () => {
    try {
      const storedProfile = await AsyncStorage.getItem('learning_profile');
      if (storedProfile) {
        setLearningProfile(JSON.parse(storedProfile));
      }
    } catch (error) {
      console.error('Error loading learning profile:', error);
    }
  };

  const updateLearningProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await LearningApiService.updateUserProfile(profileData);
      
      if (response.success) {
        const updatedProfile = { ...learningProfile, ...profileData };
        setLearningProfile(updatedProfile);
        await AsyncStorage.setItem('learning_profile', JSON.stringify(updatedProfile));
        return { success: true };
      }
      
      throw new Error(response.message || 'Failed to update profile');
    } catch (error) {
      console.error('Error updating learning profile:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Learning statistics
  const loadLearningStats = async () => {
    try {
      const response = await LearningApiService.getLearningStats();
      if (response.success) {
        setLearningStats(response.data);
        await AsyncStorage.setItem('learning_stats', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading learning stats:', error);
      // Load from cache if API fails
      const cachedStats = await AsyncStorage.getItem('learning_stats');
      if (cachedStats) {
        setLearningStats(JSON.parse(cachedStats));
      }
    }
  };

  const updateLearningProgress = async (courseId, progress) => {
    try {
      const response = await LearningApiService.updateCourseProgress(courseId, progress);
      
      if (response.success) {
        // Update enrolled courses with new progress
        setEnrolledCourses(prev => 
          prev.map(course => 
            course.id === courseId 
              ? { ...course, progress } 
              : course
          )
        );
        
        // Refresh learning stats
        await loadLearningStats();
        return { success: true };
      }
      
      throw new Error(response.message || 'Failed to update progress');
    } catch (error) {
      console.error('Error updating learning progress:', error);
      return { success: false, error: error.message };
    }
  };

  // Course management
  const loadEnrolledCourses = async () => {
    try {
      const response = await LearningApiService.getMyCourses();
      if (response.success) {
        setEnrolledCourses(response.data);
        await AsyncStorage.setItem('enrolled_courses', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading enrolled courses:', error);
      // Load from cache if API fails
      const cachedCourses = await AsyncStorage.getItem('enrolled_courses');
      if (cachedCourses) {
        setEnrolledCourses(JSON.parse(cachedCourses));
      }
    }
  };

  const loadAvailableCourses = async (filters = {}) => {
    try {
      const response = await LearningApiService.getAllCourses(filters);
      if (response.success) {
        setAvailableCourses(response.data);
        await AsyncStorage.setItem('available_courses', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading available courses:', error);
      // Load from cache if API fails
      const cachedCourses = await AsyncStorage.getItem('available_courses');
      if (cachedCourses) {
        setAvailableCourses(JSON.parse(cachedCourses));
      }
    }
  };

  const enrollInCourse = async (courseId) => {
    try {
      setLoading(true);
      const response = await LearningApiService.enrollInCourse(courseId);
      
      if (response.success) {
        // Add course to enrolled courses
        const course = availableCourses.find(c => c.id === courseId);
        if (course) {
          const enrolledCourse = { ...course, progress: 0 };
          setEnrolledCourses(prev => [...prev, enrolledCourse]);
        }
        
        // Refresh data
        await loadEnrolledCourses();
        await loadLearningStats();
        
        return { success: true, message: 'Successfully enrolled in course' };
      }
      
      throw new Error(response.message || 'Failed to enroll in course');
    } catch (error) {
      console.error('Error enrolling in course:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getCourseDetails = async (courseId) => {
    try {
      const response = await LearningApiService.getCourseDetails(courseId);
      if (response.success) {
        setCurrentCourse(response.data);
        return response.data;
      }
      throw new Error(response.message || 'Failed to get course details');
    } catch (error) {
      console.error('Error getting course details:', error);
      setError(error.message);
      return null;
    }
  };

  // Content management
  const loadEngineeringBooks = async (filters = {}) => {
    try {
      const response = await LearningApiService.getEngineeringBooks(filters);
      if (response.success) {
        setEngineeringBooks(response.data);
        await AsyncStorage.setItem('engineering_books', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading engineering books:', error);
      // Load from cache if API fails
      const cachedBooks = await AsyncStorage.getItem('engineering_books');
      if (cachedBooks) {
        setEngineeringBooks(JSON.parse(cachedBooks));
      }
    }
  };

  const loadVirtualInternships = async (filters = {}) => {
    try {
      const response = await LearningApiService.getVirtualInternships(filters);
      if (response.success) {
        setVirtualInternships(response.data);
        await AsyncStorage.setItem('virtual_internships', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading virtual internships:', error);
      // Load from cache if API fails
      const cachedInternships = await AsyncStorage.getItem('virtual_internships');
      if (cachedInternships) {
        setVirtualInternships(JSON.parse(cachedInternships));
      }
    }
  };

  const applyForInternship = async (internshipId) => {
    try {
      setLoading(true);
      const response = await LearningApiService.applyForInternship(internshipId);
      
      if (response.success) {
        return { success: true, message: 'Application submitted successfully' };
      }
      
      throw new Error(response.message || 'Failed to apply for internship');
    } catch (error) {
      console.error('Error applying for internship:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Achievements
  const loadAchievements = async () => {
    try {
      const response = await LearningApiService.getAchievements();
      if (response.success) {
        setAchievements(response.data);
        await AsyncStorage.setItem('achievements', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
      // Load from cache if API fails
      const cachedAchievements = await AsyncStorage.getItem('achievements');
      if (cachedAchievements) {
        setAchievements(JSON.parse(cachedAchievements));
      }
    }
  };

  // Search functionality
  const searchContent = async (query, filters = {}) => {
    try {
      setLoading(true);
      const response = await LearningApiService.searchContent(query, filters);
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error(response.message || 'Search failed');
    } catch (error) {
      console.error('Error searching content:', error);
      setError(error.message);
      return { courses: [], books: [], internships: [] };
    } finally {
      setLoading(false);
    }
  };

  // Refresh all data
  const refreshAllData = async () => {
    try {
      setRefreshing(true);
      await initializeLearningData();
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError(error.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  const value = {
    // State
    learningProfile,
    learningStats,
    achievements,
    enrolledCourses,
    availableCourses,
    currentCourse,
    engineeringBooks,
    virtualInternships,
    loading,
    error,
    refreshing,

    // Profile actions
    updateLearningProfile,
    loadLearningProfile,

    // Learning actions
    updateLearningProgress,
    loadLearningStats,

    // Course actions
    loadAvailableCourses,
    loadEnrolledCourses,
    enrollInCourse,
    getCourseDetails,

    // Content actions
    loadEngineeringBooks,
    loadVirtualInternships,
    applyForInternship,

    // Achievement actions
    loadAchievements,

    // Search actions
    searchContent,

    // Utility actions
    refreshAllData,
    clearError,
    initializeLearningData,
  };

  return (
    <LearningContext.Provider value={value}>
      {children}
    </LearningContext.Provider>
  );
};

export default LearningContext;

